import {
  RangeDirectionChange,
  useBearingsTableStore,
} from '@hub/bearing-hub/BearingHubPage/BearingsTable/useBearingsTableStore';
import { Icon, IconID } from '@hub/common/Icon/Icon';
import type { ShapeDescriptor } from '@hub/bearing-hub/common/shapes/shapes';
import type { IbhDimensions } from '@hub/bearing-hub/common/dimensions/dimensions';
import { getDimensionLabel } from '@hub/bearing-hub/common/dimensions/dimensions';
import type { IbhPartsResponseDto } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { UnitSystem } from '@igus/kopla-domain';
import { createNumberFormatter } from '@icommerce/i18n';

export interface BearingTableDimensionProps {
  data: IbhPartsResponseDto;
  selectedShape: ShapeDescriptor;
  locale: string;
  showHeader?: boolean;
  usedDimensions?: IbhDimensions;
  unitSystem: UnitSystem;
}

export const BearingTableDimensions = ({
  showHeader = false,
  selectedShape,
  data,
  locale,
  usedDimensions,
  unitSystem,
}: BearingTableDimensionProps) => {
  const {
    shapeDimensionsHeadersKeysToDisplay,
    presentDimensionsDataFrom,
    presentDimensionsDataTo,
    allShapeDimensionsHeadersKeys,
    updateChangeRangeDirection,
  } = useBearingsTableStore();
  return (
    <div className="flex flex-col overflow-auto relative w-full">
      {showHeader && (
        <div className="relative overflow-hidden">
          <div
            className={`absolute left-[-5px] top-[1px] bottom-0.5 cursor-pointer ${
              presentDimensionsDataFrom === 0 ? 'invisible' : 'visible'
            }`}
            onClick={() => updateChangeRangeDirection(RangeDirectionChange.Back)}
          >
            <Icon size={8} id={IconID.CHEVRON_LEFT} />
          </div>
          <div className="flex flex-row leading-[2.4] items-center w-full border-b-[1px] border-solid border-[#AFAFAF]">
            {shapeDimensionsHeadersKeysToDisplay.map(definition => (
              <div
                key={getDimensionLabel(definition, selectedShape.shapeKey)}
                className="text-xs font-medium grow basis-0 text-center xs:min-w-[50px]"
              >
                {getDimensionLabel(definition, selectedShape.shapeKey)}
              </div>
            ))}
          </div>
          <div
            className={`absolute bottom-0.5 cursor-pointer right-[-5px] top-[1px] ${
              presentDimensionsDataTo >= allShapeDimensionsHeadersKeys.length ? 'invisible' : 'visible'
            }`}
            onClick={() => updateChangeRangeDirection(RangeDirectionChange.Forward)}
          >
            <Icon size={8} id={IconID.CHEVRON_RIGHT} />
          </div>
        </div>
      )}
      <div
        className={`flex flex-row leading-[2.4] items-center w-full justify-center${
          showHeader ? 'border-b-[1px] border-solid border-[#AFAFAF]' : ''
        }`}
      >
        {shapeDimensionsHeadersKeysToDisplay.map(label => {
          let doMark = false;
          const rawValue = data.dimensions[label as keyof IbhDimensions];
          if (usedDimensions) {
            const usedDimensionValue = usedDimensions[label as keyof IbhDimensions];
            const newDimensionValue = data.dimensions[label as keyof IbhDimensions];

            if (usedDimensionValue === newDimensionValue) {
              doMark = true;
            }
          }
          const hasValidNumber = Number.isFinite(Number(rawValue));

          const formattedValue = hasValidNumber
            ? createNumberFormatter(locale, {
                maximumFractionDigits: unitSystem === UnitSystem.imperial ? 4 : 2,
                minimumFractionDigits: unitSystem === UnitSystem.imperial ? 4 : 2,
              }).format(Number(rawValue))
            : '-';

          return (
            <div
              key={label}
              className="whitespace-nowrap text-xs grow text-center lg:min-w-0 min-w-[50px] w-full"
              data-testid={`dimension-${label}`}
            >
              <span className={doMark ? 'text-[#1C8B41]' : ''}>{formattedValue}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
